import {
  collection,
  doc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { firestore } from './client';
import {
  ICoupon,
  ICouponUsage,
  ICouponValidationResult,
} from 'utils/types/Coupon';
import { couponCache } from './couponCache';

/**
 * Busca um cupom pelo código
 */
export const getCouponByCode = async (
  code: string,
): Promise<ICoupon | null> => {
  const cacheKey = `coupon:${code.toUpperCase()}`;

  // Verificar cache primeiro
  const cached = couponCache.get<ICoupon>(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const couponsRef = collection(firestore, 'coupons');
    const q = query(couponsRef, where('code', '==', code.toUpperCase()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // Cache resultado negativo por 2 minutos
      couponCache.set(cacheKey, null, 2 * 60 * 1000);
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();

    const coupon: ICoupon = {
      id: doc.id,
      code: data.code,
      type: data.type,
      value: data.value,
      isActive: data.isActive,
      description: data.description,
      validFrom: data.validFrom.toDate(),
      validUntil: data.validUntil.toDate(),
      maxUses: data.maxUses,
      currentUses: data.currentUses || 0,
      minCartValue: data.minCartValue,
      maxDiscountValue: data.maxDiscountValue,
      applicableProducts: data.applicableProducts,
      userRestrictions: data.userRestrictions,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };

    // Cache cupom válido por 5 minutos
    couponCache.set(cacheKey, coupon, 5 * 60 * 1000);

    return coupon;
  } catch (error) {
    console.error('Erro ao buscar cupom:', error);
    return null;
  }
};

/**
 * Verifica quantas vezes um usuário usou um cupom específico
 */
export const getUserCouponUsageCount = async (
  userId: string,
  couponId: string,
): Promise<number> => {
  try {
    const usageRef = collection(firestore, 'coupon_usage');
    const q = query(
      usageRef,
      where('userId', '==', userId),
      where('couponId', '==', couponId),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Erro ao verificar uso do cupom pelo usuário:', error);
    return 0;
  }
};

/**
 * Valida um cupom considerando todas as regras
 */
export const validateCoupon = async (
  code: string,
  cartValue: number,
  userId?: string,
  isSubscriber: boolean = false,
): Promise<ICouponValidationResult> => {
  try {
    // Busca o cupom
    const coupon = await getCouponByCode(code);

    if (!coupon) {
      return {
        isValid: false,
        error: 'Cupom não encontrado',
      };
    }

    // Verifica se está ativo
    if (!coupon.isActive) {
      return {
        isValid: false,
        error: 'Cupom não está ativo',
      };
    }

    // Verifica validade por data
    const now = new Date();
    if (now < coupon.validFrom || now > coupon.validUntil) {
      return {
        isValid: false,
        error: 'Cupom fora do período de validade',
      };
    }

    // Verifica valor mínimo do carrinho
    if (coupon.minCartValue && cartValue < coupon.minCartValue) {
      return {
        isValid: false,
        error: `Valor mínimo do pedido: R$ ${coupon.minCartValue.toFixed(2).replace('.', ',')}`,
      };
    }

    // Verifica limite de usos totais
    if (coupon.maxUses && coupon.currentUses >= coupon.maxUses) {
      return {
        isValid: false,
        error: 'Cupom esgotado',
      };
    }

    // Verifica restrições de usuário
    if (coupon.userRestrictions) {
      const { subscribersOnly, maxUsesPerUser } = coupon.userRestrictions;

      // Só para assinantes
      if (subscribersOnly && !isSubscriber) {
        return {
          isValid: false,
          error: 'Cupom válido apenas para assinantes',
        };
      }

      // Verifica uso por usuário
      if (userId && maxUsesPerUser) {
        const userUsageCount = await getUserCouponUsageCount(userId, coupon.id);
        if (userUsageCount >= maxUsesPerUser) {
          return {
            isValid: false,
            error: 'Você já atingiu o limite de uso deste cupom',
          };
        }
      }
    }

    // Calcula o desconto
    let discountAmount = 0;
    let discountPercentage = 0;

    if (coupon.type === 'percentage') {
      discountPercentage = coupon.value;
      discountAmount = cartValue * coupon.value;

      // Aplica limite máximo de desconto se existir
      if (coupon.maxDiscountValue && discountAmount > coupon.maxDiscountValue) {
        discountAmount = coupon.maxDiscountValue;
        discountPercentage = discountAmount / cartValue;
      }
    } else {
      discountAmount = Math.min(coupon.value, cartValue);
      discountPercentage = discountAmount / cartValue;
    }

    return {
      isValid: true,
      coupon,
      discountAmount,
      discountPercentage,
    };
  } catch (error) {
    console.error('Erro ao validar cupom:', error);
    return {
      isValid: false,
      error: 'Erro interno ao validar cupom',
    };
  }
};

/**
 * Registra o uso de um cupom
 */
export const recordCouponUsage = async (
  couponId: string,
  userId: string,
  orderValue: number,
  discountApplied: number,
): Promise<void> => {
  try {
    // Registra o uso na coleção coupon_usage
    const usageData: Omit<ICouponUsage, 'id'> = {
      couponId,
      userId,
      usedAt: new Date(),
      orderValue,
      discountApplied,
    };

    await addDoc(collection(firestore, 'coupon_usage'), {
      ...usageData,
      usedAt: Timestamp.now(),
    });

    // Incrementa o contador de uso do cupom
    const couponRef = doc(firestore, 'coupons', couponId);
    await updateDoc(couponRef, {
      currentUses: increment(1),
      updatedAt: Timestamp.now(),
    });

    // Invalidar cache para forçar nova busca após uso
    couponCache.invalidate('coupon:');
  } catch (error) {
    console.error('Erro ao registrar uso do cupom:', error);
    throw error;
  }
};
